import {
  CALORIES_PER_CARBOHYDRATE_GRAM,
  CALORIES_PER_FAT_GRAM,
  CALORIES_PER_PROTEIN_GRAM,
  FOOD_EXCHANGE_MULTIPLIERS,
} from "@/config/constants";
import { MealPatternNameKind } from "@/gql/graphql";

interface PlannedMealItem1 {
  foodPortion: {
    amount: number;
    weightGrams: number;
  };
  amount: number;
}

interface PlannedMealItem2 {
  food: {
    epWeightGrams: number;
  };
  foodPortion: {
    amount: number;
    weightGrams: number;
  };
  amount: number;
}

export function getPortionWeightGrams(
  portion: PlannedMealItem1["foodPortion"],
  amount = 1
): number {
  const portionWeightPerAmount = portion.weightGrams / portion.amount;
  return portionWeightPerAmount * amount;
}

/**
 * Calculates the weight of planned meal item, accounting selected portion.
 */
export function getPlannedMealItemWeightGrams(item: PlannedMealItem1): number {
  return getPortionWeightGrams(item.foodPortion, item.amount);
}

/**
 * Returns the multiplier of planned meal item to calculate nutrients like
 * energy, carbohydrates, etc.
 */
export function getPlannedMealItemWeightMultiplier(
  item: PlannedMealItem2
): number {
  const itemWeight = getPlannedMealItemWeightGrams(item);
  return itemWeight / item.food.epWeightGrams;
}

export function mealPatternNameLabel(name: MealPatternNameKind) {
  const map = {
    [MealPatternNameKind.Fat]: "Fat",
    [MealPatternNameKind.Fruit]: "Fruit",
    [MealPatternNameKind.MeatHighFat]: "Meat - High fat",
    [MealPatternNameKind.MeatLowFat]: "Meat - Low fat",
    [MealPatternNameKind.MeatModerateFat]: "Meat - Moderate fat",
    [MealPatternNameKind.MilkLowFat]: "Milk - Low fat",
    [MealPatternNameKind.MilkNonFatSkim]: "Milk - Non-fat skim",
    [MealPatternNameKind.MilkWhole]: "Milk - Whole",
    [MealPatternNameKind.RiceHighProtein]: "Rice - High protein",
    [MealPatternNameKind.RiceLowProtein]: "Rice - Low Protein",
    [MealPatternNameKind.RiceMediumProtein]: "Rice - Medium protein",
    [MealPatternNameKind.Sugar]: "Sugar",
    [MealPatternNameKind.Vegetable]: "Vegetable",
    [MealPatternNameKind.FatsAndOil]: "Fats and Oil",
    [MealPatternNameKind.Fruits]: "Fruits",
    [MealPatternNameKind.MeatAndSeafood]: "Meat and Seafood",
    [MealPatternNameKind.Dairy]: "Dairy",
    [MealPatternNameKind.PlantBasedMeat]: "Plant-based Meat",
    [MealPatternNameKind.Starches]: "Starches",
    [MealPatternNameKind.Treats]: "Treats",
    [MealPatternNameKind.Vegetables]: "Vegetables",
  };
  return map[name];
}

const foodNameMapping: Record<string, MealPatternNameKind> = {
  "Fats and oil": MealPatternNameKind.FatsAndOil,
  Fruits: MealPatternNameKind.Fruits,
  "Meat and Seafood": MealPatternNameKind.MeatAndSeafood,
  Dairy: MealPatternNameKind.Dairy,
  "Plant-based Meat": MealPatternNameKind.PlantBasedMeat,
  Starches: MealPatternNameKind.Starches,
  Treats: MealPatternNameKind.Treats,
  Vegetables: MealPatternNameKind.Vegetables,
};

export function foodNameToMealPatternKind(name: string): MealPatternNameKind {
  return foodNameMapping[name];
}
//Todo: This thing is still being used by PDF renderers, replace it by actual simple food macro values
export function mealPatternMultiplier(name: MealPatternNameKind) {
  const map = {
    [MealPatternNameKind.Fat]: FOOD_EXCHANGE_MULTIPLIERS.fat,
    [MealPatternNameKind.Fruit]: FOOD_EXCHANGE_MULTIPLIERS.fruit,
    [MealPatternNameKind.MeatHighFat]: FOOD_EXCHANGE_MULTIPLIERS.meatHighFat,
    [MealPatternNameKind.MeatLowFat]: FOOD_EXCHANGE_MULTIPLIERS.meatLowFat,
    [MealPatternNameKind.MeatModerateFat]:
      FOOD_EXCHANGE_MULTIPLIERS.meatModerateFat,
    [MealPatternNameKind.MilkLowFat]: FOOD_EXCHANGE_MULTIPLIERS.milkLowFat,
    [MealPatternNameKind.MilkNonFatSkim]:
      FOOD_EXCHANGE_MULTIPLIERS.milkNonFatSkim,
    [MealPatternNameKind.MilkWhole]: FOOD_EXCHANGE_MULTIPLIERS.milkWhole,
    [MealPatternNameKind.RiceHighProtein]:
      FOOD_EXCHANGE_MULTIPLIERS.riceHighProtein,
    [MealPatternNameKind.RiceLowProtein]:
      FOOD_EXCHANGE_MULTIPLIERS.riceLowProtein,
    [MealPatternNameKind.RiceMediumProtein]:
      FOOD_EXCHANGE_MULTIPLIERS.riceMediumProtein,
    [MealPatternNameKind.Sugar]: FOOD_EXCHANGE_MULTIPLIERS.sugar,
    [MealPatternNameKind.Vegetable]: FOOD_EXCHANGE_MULTIPLIERS.vegetable,
    [MealPatternNameKind.FatsAndOil]: FOOD_EXCHANGE_MULTIPLIERS.fatsAndOil,
    [MealPatternNameKind.Dairy]: FOOD_EXCHANGE_MULTIPLIERS.dairy,
    [MealPatternNameKind.Fruits]: FOOD_EXCHANGE_MULTIPLIERS.fruits,
    [MealPatternNameKind.MeatAndSeafood]:
      FOOD_EXCHANGE_MULTIPLIERS.meatAndSeafood,
    [MealPatternNameKind.PlantBasedMeat]:
      FOOD_EXCHANGE_MULTIPLIERS.plantBasedMeat,
    [MealPatternNameKind.Starches]: FOOD_EXCHANGE_MULTIPLIERS.starches,
    [MealPatternNameKind.Treats]: FOOD_EXCHANGE_MULTIPLIERS.treats,
    [MealPatternNameKind.Vegetables]: FOOD_EXCHANGE_MULTIPLIERS.vegetables,
  };
  return map[name];
}

export function mealPatternMacronutrients(
  name: MealPatternNameKind,
  amountServings: number
) {
  const multiplier = mealPatternMultiplier(name);
  const energyCal = multiplier.calories * amountServings;

  const carbGrams = multiplier.carbohydrates * amountServings;
  const proteinGrams = multiplier.proteins * amountServings;
  const fatGrams = multiplier.fats * amountServings;

  const carbPercent = (carbGrams * CALORIES_PER_CARBOHYDRATE_GRAM) / energyCal;
  const proteinPercent = (proteinGrams * CALORIES_PER_PROTEIN_GRAM) / energyCal;
  const fatPercent = (fatGrams * CALORIES_PER_FAT_GRAM) / energyCal;

  return {
    energyKcal: energyCal,
    carbohydrates: { grams: carbGrams, percentage: carbPercent },
    proteins: { grams: proteinGrams, percentage: proteinPercent },
    fats: { grams: fatGrams, percentage: fatPercent },
  };
}
